import React, { useState, useEffect } from 'react';
import { Table, Card, Form, Input, Select, Button, message, Space, Radio } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { getFansList, ttCallbackConversion } from '../../../api/fans';
import { getCorpList } from '../../../api/corp';
import { useSuite } from '../../../context/SuiteContext';
import { copyToClipboard } from '../../../utils/clipboard';
import './FansDetail.css';

const { Option } = Select;

const FansList = () => {
    const { suiteId } = useSuite();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [corpList, setCorpList] = useState([]);

    // Search type states
    const [linkSearchType, setLinkSearchType] = useState('name'); // 'name' or 'id'
    const [customerSearchType, setCustomerSearchType] = useState('name'); // 'name' or 'id'

    const [form] = Form.useForm();

    // Fetch corp list
    const fetchCorpList = async () => {
        try {
            const res = await getCorpList({ suite_id: suiteId });
            if (res.code === 0) {
                setCorpList(res.data.list);
                // Set default corp in search form
                setCorpList(res.data.list);
                // Default no corp selection

            } else {
                message.error(res.message || '获取企业列表失败');
            }
        } catch (error) {
            console.error('Fetch corp list failed:', error);
            message.error(error.message || '获取企业列表失败');
        }
    };

    // Fetch fans list
    const fetchFans = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const values = await form.validateFields();
            const params = {
                page,
                page_size: pageSize,
                suite_id: suiteId,
                corp_id: values.corp_id,
            };

            // Add link search param based on type
            if (values.linkSearch) {
                if (linkSearchType === 'name') {
                    params.link_name = values.linkSearch;
                } else {
                    params.link_id = values.linkSearch;
                }
            }

            // Add customer search param based on type
            if (values.customerSearch) {
                if (customerSearchType === 'name') {
                    params.fans_name = values.customerSearch;
                } else {
                    params.external_user_id = values.customerSearch;
                }
            }

            // Add employee ID if provided
            if (values.userid) {
                params.userid = values.userid;
            }

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });

            const res = await getFansList(params);
            if (res.code === 0) {
                setData(res.data.list || []);
                setPagination({
                    ...pagination,
                    current: res.data.page,
                    pageSize: res.data.page_size,
                    total: res.data.total,
                });
            } else {
                message.error(res.message || '获取加粉列表失败');
            }
        } catch (error) {
            console.error('Fetch fans failed:', error);
            message.error(error.message || '获取加粉列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (suiteId) {
            fetchCorpList();
        }
    }, [suiteId]);

    useEffect(() => {
        if (suiteId && corpList.length > 0) {
            fetchFans();
        }
    }, [suiteId, corpList]);

    const handleTableChange = (newPagination) => {
        fetchFans(newPagination.current, newPagination.pageSize);
    };

    const onFinish = () => {
        fetchFans(1, pagination.pageSize);
    };

    const onReset = () => {
        form.resetFields();
        form.resetFields();

        setLinkSearchType('name');
        setCustomerSearchType('name');
        fetchFans(1, pagination.pageSize);
    };

    // Handle callback conversion
    const handleCallback = async (record) => {
        try {
            const res = await ttCallbackConversion({
                id: record.id,
                // external_userid: record.external_userid,
                // corp_id: record.corp_id,
                // suite_id: suiteId
            });

            if (res.code === 0) {
                message.success('手动回传成功');
                // Refresh the list
                fetchFans(pagination.current, pagination.pageSize);
            } else {
                message.error(res.message || '回传失败');
            }
        } catch (error) {
            console.error('Callback conversion failed:', error);
            message.error(error.message || '回传失败');
        }
    };

    const columns = [
        {
            title: '客户信息',
            key: 'customer_info',
            width: 180,
            fixed: 'left',
            render: (_, record) => (
                <div>
                    <div>{record.fans_name || '-'}</div>
                    <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center' }}>
                        <div style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.external_user_id}>
                            {record.external_user_id || '-'}
                        </div>
                        {record.external_user_id && (
                            <CopyOutlined
                                style={{ marginLeft: 4, cursor: 'pointer', color: '#1890ff' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(record.external_user_id);
                                }}
                            />
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: '所属企业',
            key: 'corp_info',
            width: 180,
            render: (_, record) => (
                <div>
                    <div>{record.corp_name || '-'}</div>
                    <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center' }}>
                        <div style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.corp_id}>
                            {record.corp_id || '-'}
                        </div>
                        {record.corp_id && (
                            <CopyOutlined
                                style={{ marginLeft: 4, cursor: 'pointer', color: '#1890ff' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(record.corp_id);
                                }}
                            />
                        )}
                    </div>
                </div>
            ),
        },
        // {
        //     title: '获客链接ID',
        //     dataIndex: 'link_id',
        //     key: 'link_id',
        //     align: 'center',
        // },
        {
            title: '获客链接',
            key: 'link_info',
            width: 180,
            render: (_, record) => (
                <div>
                    <div>{record.link_name || '-'}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{record.link_id || '-'}</div>
                </div>
            ),
        },
        {
            title: '接粉客服',
            key: 'employee_info',
            width: 180,
            // align: 'center',
            render: (_, record) => (
                <div>
                    <div>{record.username || '-'}</div>
                    <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center' }}>
                        <div style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.userid}>
                            {record.userid || '-'}
                        </div>
                        {record.userid && (
                            <CopyOutlined
                                style={{ marginLeft: 4, cursor: 'pointer', color: '#1890ff' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(record.userid);
                                }}
                            />
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: '是否回传',
            dataIndex: 'tt_callback',
            key: 'tt_callback',
            align: 'center',
            width: 100,
            render: (callback) => (
                <span style={{ color: callback === 1 ? '#52c41a' : '#ff4d4f' }}>
                    {callback === 1 ? '已回传' : '未回传'}
                </span>
            ),
        },

        {
            title: '添加时间',
            dataIndex: 'add_time',
            key: 'add_time',
            align: 'center',
            width: 180,
            render: (text) => {
                if (!text) return '-';
                // valid unix timestamp (10 digits) usually seconds
                const date = new Date(text * 1000);
                return date.toLocaleString();
            }
        },
        {
            title: '客户说话',
            dataIndex: 'start_chat',
            key: 'start_chat',
            align: 'center',
            width: 120,
            render: (startChat) => {
                const isSpoken = startChat === 1;
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: isSpoken ? '#52c41a' : '#bfbfbf',
                            marginRight: 6
                        }} />
                        <span style={{ color: isSpoken ? '#52c41a' : '#bfbfbf' }}>
                            {isSpoken ? '已开口' : '未开口'}
                        </span>
                    </div>
                );
            }
        },
        {
            title: '删除类型',
            dataIndex: 'del_type',
            key: 'del_type',
            align: 'center',
            width: 120,
            render: (delType) => {
                if (!delType || delType === 0) {
                    return '-';
                }
                const text = delType === 1 ? '客服删除用户' : '被用户删除';
                return <span style={{ color: '#ff4d4f' }}>{text}</span>;
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                record.tt_callback === 0 ? (
                    <span className="action-link" onClick={() => handleCallback(record)}>
                        手动回传
                    </span>
                ) : null
            ),
        },
    ];

    return (
        <div className="fans-detail-container">
            <div className="wecom-header">
                <span>加粉明细</span>
            </div>

            <Card bordered={false} className="search-card">
                <Form
                    form={form}
                    layout="inline"
                    onFinish={onFinish}
                >
                    <Form.Item name="corp_id">
                        <Select
                            placeholder="请选择企业"
                            showSearch
                            optionFilterProp="children"
                            style={{ width: 200 }}
                        >
                            {corpList.map(corp => (
                                <Option key={corp.id} value={corp.corp_id}>
                                    {corp.corp_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="userid">
                        <Input placeholder="输入员工ID" allowClear style={{ width: 200 }} />
                    </Form.Item>

                    <Form.Item>
                        <div className="search-switcher">
                            <Select
                                value={linkSearchType}
                                onChange={setLinkSearchType}
                                style={{ width: 140 }}
                            >
                                <Option value="name">获客链接</Option>
                                <Option value="id">获客链接ID</Option>
                            </Select>
                            <Form.Item name="linkSearch" noStyle>
                                <Input
                                    placeholder={linkSearchType === 'name' ? '输入获客链接名称' : '输入获客链接ID'}
                                    allowClear
                                    style={{ width: 200 }}
                                />
                            </Form.Item>
                        </div>
                    </Form.Item>

                    <Form.Item>
                        <div className="search-switcher">
                            <Select
                                value={customerSearchType}
                                onChange={setCustomerSearchType}
                                style={{ width: 140 }}
                            >
                                <Option value="name">客户名称</Option>
                                <Option value="id">客户ID</Option>
                            </Select>
                            <Form.Item name="customerSearch" noStyle>
                                <Input
                                    placeholder={customerSearchType === 'name' ? '输入客户名称' : '输入客户ID'}
                                    allowClear
                                    style={{ width: 200 }}
                                />
                            </Form.Item>
                        </div>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading} className="orange-button">
                                查询
                            </Button>
                            <Button onClick={onReset}>重置</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card bordered={false} style={{ marginTop: 16 }}>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        showTotal: (total) => `共 ${total} 条`,
                        showSizeChanger: true,
                        showQuickJumper: true,
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{ x: 1300 }}
                />
            </Card>
        </div>
    );
};

export default FansList;
